import { InnerBox } from "@/components/elements/InnerBox";
import { Grid, Stack } from "@mui/material";
import { FC } from "react";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { useEnum } from "@/api/services/global/enum";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { JobInfoItem } from "@/features/job/components/job-detail/JobInfoItem";
import { findLabelsByValue } from "@/utils/object";
import { convertNumberToCommas } from "@/utils/input";
import { useGetRecruitmentSourceList } from "@/api/services/main/recruitment-source";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";

type TJobBasicInfoDetailProps = {
  defaultValues?: Partial<TJobForm>;
  isDraft?: boolean;
};

export const JobBasicInfoDetail: FC<TJobBasicInfoDetailProps> = ({ defaultValues, isDraft }) => {
  const { data: clientTypeOptions } = useEnum("EClientType");
  const { isLoading: isLoadingCompanySpecificIndustry, getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry } =
    useGetCompanySpecificIndustryList();
  const { recruitmentSourceOptions, isLoading: isLoadingRecruitmentSource } = useGetRecruitmentSourceList();

  const getCompanySpecificBusinessClassificationLabels = () => {
    if (!isLoadingCompanySpecificIndustry && defaultValues?.companySpecificIndustryId) {
      const companySpecificBusinessClassificationList = getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry(
        defaultValues?.companySpecificIndustryId,
      );
      return findLabelsByValue(companySpecificBusinessClassificationList, defaultValues?.companySpecificBusinessClassificationIds ?? []);
    }
    return null;
  };

  const companyDetailUrl = defaultValues?.company?.id ? getDynamicRoute(AppRoutes.companyDetail, { companyId: defaultValues.company.id }) : undefined;

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="基本情報" />} padding="24px" showDivider={false}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <JobInfoItem
              label="企業名"
              value={defaultValues?.company?.companyName}
              required={!isDraft}
              type={companyDetailUrl ? "internalLink" : undefined}
              internalLinkTo={companyDetailUrl}
            />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="求人管理番号" value={defaultValues?.managementNumber} />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem label="求人名" value={defaultValues?.jobName} required />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="分野" value={defaultValues?.companySpecificIndustry?.companySpecificIndustry} required={!isDraft} />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="業務区分" value={getCompanySpecificBusinessClassificationLabels()} />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem label="取扱業務等の区分" value={defaultValues?.businessClassification?.businessCategory} required={!isDraft} />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem
              label="募集人数"
              value={
                defaultValues?.recruitmentNumber !== null && defaultValues?.recruitmentNumber !== undefined
                  ? `${defaultValues.recruitmentNumber}人`
                  : ""
              }
              required={!isDraft}
            />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem
              label="紹介料（売上）"
              value={
                defaultValues?.referralFee !== null && defaultValues?.referralFee !== undefined
                  ? `${convertNumberToCommas(defaultValues.referralFee.toString())}円`
                  : ""
              }
            />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem
              label="紹介料（仕入）"
              value={
                defaultValues?.referralFeeCost !== null && defaultValues?.referralFeeCost !== undefined
                  ? `${convertNumberToCommas(defaultValues.referralFeeCost.toString())}円`
                  : ""
              }
            />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="求人獲得ルート" value={findLabelsByValue(recruitmentSourceOptions, defaultValues?.recruitmentSourceIds ?? [])} />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="顧客区分" value={findLabelsByValue(clientTypeOptions, defaultValues?.clientType ?? [])} />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem label="求人担当者コメント" value={defaultValues?.recruiterNote} isShowDivider={false} />
          </Grid>
        </Grid>
      </InnerBox>
    </Stack>
  );
};
